// Google Places (New) API — text search + details + upsert to Supabase.
//
// IMPORTANT: Restrict this key by HTTP referrer in the Google Cloud Console.
// We only use the (New) Places API — REST endpoints at /v1.

import { supabase } from "@/integrations/supabase/client";

export const GOOGLE_PLACES_API_KEY = "AIzaSyDApbJcNmIcV1Dp5oa5oewjFiULX2PX2ow";
export const PLACES_BASE_URL = "https://places.googleapis.com/v1";

export type GooglePlace = {
  place_id: string;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  price_level: number | null;
  photo_url: string | null;
  editorial_summary: string | null;
  primary_type: string | null;
  types: string[];
};

const TEXT_SEARCH_FIELDS = [
  "places.id",
  "places.displayName",
  "places.formattedAddress",
  "places.location",
  "places.priceLevel",
  "places.photos",
  "places.editorialSummary",
  "places.rating",
  "places.userRatingCount",
  "places.primaryType",
  "places.types",
].join(",");

const DETAILS_FIELDS = [
  "id",
  "displayName",
  "formattedAddress",
  "location",
  "priceLevel",
  "photos",
  "editorialSummary",
  "rating",
  "userRatingCount",
  "primaryType",
  "types",
].join(",");

// Maps Google Places primaryType / types → our cuisine_types.slug.
// If no mapping is found, no row is inserted (better empty than wrong).
export const GOOGLE_TYPE_TO_CUISINE_SLUG: Record<string, string> = {
  italian_restaurant: "italian",
  japanese_restaurant: "japanese",
  chinese_restaurant: "chinese",
  indian_restaurant: "indian",
  thai_restaurant: "thai",
  french_restaurant: "french",
  mexican_restaurant: "mexican",
  american_restaurant: "american",
  korean_restaurant: "korean",
  turkish_restaurant: "turkish",
  mediterranean_restaurant: "mediterranean",
  pizza_restaurant: "italian",
  sushi_restaurant: "japanese",
  hamburger_restaurant: "american",
  burger_restaurant: "american",
  kebab_restaurant: "turkish",
  fish_and_chips_restaurant: "british",
  fish_and_chip_shop: "british",
  pub: "british",
  gastropub: "british",
  fast_food_restaurant: "american",
  sandwich_shop: "american",
};

// Types we explicitly skip — too broad/generic to map to a single cuisine.
export const GOOGLE_TYPE_SKIP: Set<string> = new Set([
  "takeaway_restaurant",
  "restaurant",
  "food",
  "seafood_restaurant",
]);

// Generic fallback mappings — only used if no more specific type matched.
const GOOGLE_TYPE_FALLBACK: Set<string> = new Set([
  "fast_food_restaurant",
  "sandwich_shop",
]);

function cuisineSlugForPlace(place: GooglePlace): string | null {
  const candidates = [place.primary_type, ...(place.types ?? [])].filter(
    Boolean,
  ) as string[];
  // Pass 1: specific matches only — prefer these over generic fallbacks.
  for (const t of candidates) {
    if (GOOGLE_TYPE_SKIP.has(t)) continue;
    if (GOOGLE_TYPE_FALLBACK.has(t)) continue;
    const slug = GOOGLE_TYPE_TO_CUISINE_SLUG[t];
    if (slug) return slug;
  }
  // Pass 2: generic fallbacks (fast_food_restaurant, sandwich_shop).
  for (const t of candidates) {
    if (GOOGLE_TYPE_SKIP.has(t)) continue;
    if (!GOOGLE_TYPE_FALLBACK.has(t)) continue;
    const slug = GOOGLE_TYPE_TO_CUISINE_SLUG[t];
    if (slug) return slug;
  }
  return null;
}



const PRICE_LEVEL_MAP: Record<string, number> = {
  PRICE_LEVEL_FREE: 1,
  PRICE_LEVEL_INEXPENSIVE: 1,
  PRICE_LEVEL_MODERATE: 2,
  PRICE_LEVEL_EXPENSIVE: 3,
  PRICE_LEVEL_VERY_EXPENSIVE: 4,
};

function photoUrlFromName(name: string | undefined): string | null {
  if (!name) return null;
  return `${PLACES_BASE_URL}/${name}/media?maxHeightPx=600&maxWidthPx=800&key=${GOOGLE_PLACES_API_KEY}`;
}

function normalizePlace(raw: any): GooglePlace {
  return {
    place_id: raw.id,
    name: raw.displayName?.text ?? "Unknown",
    address: raw.formattedAddress ?? null,
    lat: raw.location?.latitude ?? 0,
    lng: raw.location?.longitude ?? 0,
    price_level: raw.priceLevel ? PRICE_LEVEL_MAP[raw.priceLevel] ?? null : null,
    photo_url: photoUrlFromName(raw.photos?.[0]?.name),
    editorial_summary: raw.editorialSummary?.text ?? null,
    primary_type: raw.primaryType ?? null,
    types: Array.isArray(raw.types) ? raw.types : [],
  };
}


export async function searchGooglePlaces(
  query: string,
  lat?: number,
  lng?: number,
  radius_metres = 5000,
): Promise<GooglePlace[]> {
  if (!query.trim()) return [];
  const body: any = {
    textQuery: query,
    includedType: "restaurant",
    maxResultCount: 10,
  };
  if (typeof lat === "number" && typeof lng === "number") {
    body.locationBias = {
      circle: {
        center: { latitude: lat, longitude: lng },
        radius: radius_metres,
      },
    };
  }

  try {
    const res = await fetch(`${PLACES_BASE_URL}/places:searchText`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": GOOGLE_PLACES_API_KEY,
        "X-Goog-FieldMask": TEXT_SEARCH_FIELDS,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      console.warn("Google Places searchText failed", res.status, await res.text());
      return [];
    }
    const data = await res.json();
    return (data.places ?? []).map(normalizePlace);
  } catch (err) {
    console.warn("Google Places searchText error", err);
    return [];
  }
}

export async function getPlaceDetails(
  google_place_id: string,
): Promise<GooglePlace | null> {
  try {
    const res = await fetch(`${PLACES_BASE_URL}/places/${google_place_id}`, {
      headers: {
        "X-Goog-Api-Key": GOOGLE_PLACES_API_KEY,
        "X-Goog-FieldMask": DETAILS_FIELDS,
      },
    });
    const rawText = await res.text();
    console.log(
      "[getPlaceDetails] status",
      res.status,
      "for",
      google_place_id,
    );
    console.log("[getPlaceDetails] body", rawText);
    if (!res.ok) {
      console.warn("[getPlaceDetails] non-ok response", res.status, rawText);
      return null;
    }
    const data = JSON.parse(rawText);
    const normalized = normalizePlace(data);
    console.log("[getPlaceDetails] normalized", normalized);
    return normalized;
  } catch (err) {
    console.warn("[getPlaceDetails] threw", err);
    return null;
  }
}

// ---------------------------------------------------------------
// Extended details for the restaurant detail sheet — phone, website,
// hours, rating, reviews. Returns null on any failure so callers can
// silently hide the affected sections.
// ---------------------------------------------------------------
export type PlaceOpeningHours = {
  openNow?: boolean;
  weekdayDescriptions?: string[];
  periods?: Array<{
    open?: { day: number; hour: number; minute: number };
    close?: { day: number; hour: number; minute: number };
  }>;
};

export type PlaceReview = {
  name?: string;
  rating?: number;
  text?: { text?: string; languageCode?: string };
  originalText?: { text?: string; languageCode?: string };
  relativePublishTimeDescription?: string;
  publishTime?: string;
  authorAttribution?: {
    displayName?: string;
    uri?: string;
    photoUri?: string;
  };
};

export type PlaceExtendedDetails = {
  nationalPhoneNumber: string | null;
  internationalPhoneNumber: string | null;
  websiteUri: string | null;
  regularOpeningHours: PlaceOpeningHours | null;
  rating: number | null;
  userRatingCount: number | null;
  reviews: PlaceReview[];
};

const EXTENDED_DETAILS_FIELDS = [
  "nationalPhoneNumber",
  "internationalPhoneNumber",
  "websiteUri",
  "regularOpeningHours",
  "rating",
  "userRatingCount",
  "reviews",
].join(",");

export async function getPlaceExtendedDetails(
  google_place_id: string,
): Promise<PlaceExtendedDetails | null> {
  if (!google_place_id) return null;
  try {
    const res = await fetch(`${PLACES_BASE_URL}/places/${google_place_id}`, {
      headers: {
        "X-Goog-Api-Key": GOOGLE_PLACES_API_KEY,
        "X-Goog-FieldMask": EXTENDED_DETAILS_FIELDS,
      },
    });
    if (!res.ok) {
      console.warn("[getPlaceExtendedDetails] non-ok", res.status);
      return null;
    }
    const data = await res.json();
    return {
      nationalPhoneNumber: data.nationalPhoneNumber ?? null,
      internationalPhoneNumber: data.internationalPhoneNumber ?? null,
      websiteUri: data.websiteUri ?? null,
      regularOpeningHours: data.regularOpeningHours ?? null,
      rating: typeof data.rating === "number" ? data.rating : null,
      userRatingCount:
        typeof data.userRatingCount === "number" ? data.userRatingCount : null,
      reviews: Array.isArray(data.reviews) ? data.reviews.slice(0, 5) : [],
    };
  } catch (err) {
    console.warn("[getPlaceExtendedDetails] threw", err);
    return null;
  }
}

// Fetch a single photo URL for a place by its google_place_id. Returns null
// if the place has no photos or the request fails.
export async function getRestaurantPhotoUrl(
  googlePlaceId: string,
): Promise<string | null> {
  if (!googlePlaceId) return null;
  try {
    console.log("[getRestaurantPhotoUrl] fetching place", googlePlaceId);
    const detailsRes = await fetch(
      `${PLACES_BASE_URL}/places/${googlePlaceId}`,
      {
        headers: {
          "X-Goog-Api-Key": GOOGLE_PLACES_API_KEY,
          "X-Goog-FieldMask": "photos",
        },
      },
    );
    if (!detailsRes.ok) {
      const txt = await detailsRes.text();
      console.warn(
        "[getRestaurantPhotoUrl] details non-ok",
        detailsRes.status,
        txt,
      );
      return null;
    }
    const details = await detailsRes.json();
    console.log("[getRestaurantPhotoUrl] details response", details);
    const photoName: string | undefined = details?.photos?.[0]?.name;
    if (!photoName) {
      console.log("[getRestaurantPhotoUrl] no photos for", googlePlaceId);
      return null;
    }

    // Resolve the media URL — skipHttpRedirect returns JSON with photoUri
    // instead of a 302 redirect (which the browser can't always follow
    // cleanly in <img> tags due to CORS).
    const mediaUrl = `${PLACES_BASE_URL}/${photoName}/media?maxWidthPx=800&skipHttpRedirect=true&key=${GOOGLE_PLACES_API_KEY}`;
    console.log("[getRestaurantPhotoUrl] resolving media", mediaUrl);
    const mediaRes = await fetch(mediaUrl);
    if (!mediaRes.ok) {
      const txt = await mediaRes.text();
      console.warn(
        "[getRestaurantPhotoUrl] media non-ok",
        mediaRes.status,
        txt,
      );
      return null;
    }
    const mediaData = await mediaRes.json();
    console.log("[getRestaurantPhotoUrl] media response", mediaData);
    const photoUri: string | undefined = mediaData?.photoUri;
    if (!photoUri) {
      console.warn("[getRestaurantPhotoUrl] no photoUri in response", mediaData);
      return null;
    }
    return photoUri;
  } catch (err) {
    console.warn("[getRestaurantPhotoUrl] threw", err);
    return null;
  }
}

// ---------------------------------------------------------------
// Find the nearest city in our DB to a given (lat, lng).
// Cheap planar approximation — fine for picking between five UK cities.
// ---------------------------------------------------------------
async function nearestCityId(
  lat: number,
  lng: number,
): Promise<string | null> {
  const { data: cities } = await supabase
    .from("cities")
    .select("id, lat, lng")
    .eq("is_active", true);
  if (!cities?.length) return null;
  let bestId: string | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const c of cities) {
    const dLat = (c.lat as number) - lat;
    const dLng = (c.lng as number) - lng;
    const d = dLat * dLat + dLng * dLng;
    if (d < bestDist) {
      bestDist = d;
      bestId = c.id as string;
    }
  }
  return bestId;
}

export type RestaurantRow = {
  id: string;
  google_place_id: string | null;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  city_id: string;
  neighbourhood_id: string | null;
  price_tier: number | null;
  photo_url: string | null;
};

export async function upsertRestaurantFromPlace(
  place: GooglePlace,
): Promise<RestaurantRow | null> {
  // 1. Existing?
  const { data: existing } = await supabase
    .from("restaurants")
    .select(
      "id, google_place_id, name, address, lat, lng, city_id, neighbourhood_id, price_tier, photo_url",
    )
    .eq("google_place_id", place.place_id)
    .maybeSingle();
  if (existing) return existing as RestaurantRow;

  // 2. Nearest city
  const city_id = await nearestCityId(place.lat, place.lng);
  if (!city_id) {
    console.warn("No active city found to attach restaurant to");
    return null;
  }

  // 3. Insert
  const { data: inserted, error } = await supabase
    .from("restaurants")
    .insert({
      google_place_id: place.place_id,
      name: place.name,
      address: place.address,
      lat: place.lat,
      lng: place.lng,
      city_id,
      price_tier: place.price_level,
      photo_url: place.photo_url,
    })
    .select(
      "id, google_place_id, name, address, lat, lng, city_id, neighbourhood_id, price_tier, photo_url",
    )
    .single();
  if (error) {
    console.warn("Failed to insert restaurant", error);
    return null;
  }

  // 4. Link cuisine type (best-effort; skip if no mapping).
  await linkCuisineTypeForRestaurant((inserted as RestaurantRow).id, place);

  return inserted as RestaurantRow;
}

export async function linkCuisineTypeForRestaurant(
  restaurantId: string,
  place: GooglePlace,
): Promise<void> {
  const slug = cuisineSlugForPlace(place);
  console.log("[linkCuisine] restaurant", restaurantId, "→ slug", slug, {
    primary_type: place.primary_type,
    types: place.types,
  });
  if (!slug) {
    console.warn("[linkCuisine] no slug mapping; skipping", restaurantId);
    return;
  }
  await linkCuisineSlugForRestaurant(restaurantId, slug);
}

export async function linkCuisineSlugForRestaurant(
  restaurantId: string,
  slug: string,
): Promise<boolean> {
  const { data: cuisine, error: cuisineErr } = await supabase
    .from("cuisine_types")
    .select("id")
    .eq("slug", slug)
    .maybeSingle();
  if (cuisineErr) console.warn("[linkCuisine] cuisine lookup error", cuisineErr);
  if (!cuisine) {
    console.warn("[linkCuisine] no cuisine row found for slug", slug);
    return false;
  }
  const { data: inserted, error: insertErr } = await supabase
    .from("restaurant_cuisine_types")
    .upsert(
      { restaurant_id: restaurantId, cuisine_type_id: (cuisine as any).id },
      { onConflict: "restaurant_id,cuisine_type_id", ignoreDuplicates: true },
    )
    .select();
  if (insertErr) {
    console.error("[linkCuisine] insert error", insertErr);
    return false;
  }
  console.log("[linkCuisine] inserted/upserted", inserted);
  return true;
}

// One-shot backfill: for any restaurant with a google_place_id but no
// cuisine type rows, fetch Places details and link via the slug mapping.
// Safe to call repeatedly — it only touches rows that need fixing.
export async function backfillRestaurantCuisineLinks(): Promise<void> {
  console.log("[backfill] starting");
  const { data: rows, error } = await supabase
    .from("restaurants")
    .select("id, name, google_place_id, restaurant_cuisine_types(cuisine_type_id)")
    .not("google_place_id", "is", null)
    .eq("is_active", true);
  if (error) {
    console.error("[backfill] query error", error);
    return;
  }
  console.log("[backfill] total restaurants with google_place_id:", rows?.length ?? 0, rows);
  if (!rows?.length) return;
  const needing = (rows as any[]).filter(
    (r) => (r.restaurant_cuisine_types ?? []).length === 0,
  );
  console.log("[backfill] needing backfill:", needing.length, needing.map((r) => ({ id: r.id, name: r.name })));
  for (const r of needing) {
    try {
      console.log("[backfill] processing", r.id, r.name, "place_id:", r.google_place_id);
      const place = await getPlaceDetails(r.google_place_id);
      if (!place) {
        console.warn("[backfill] no place details returned for", r.id);
        continue;
      }
      console.log("[backfill] calling linkCuisineTypeForRestaurant for", r.id);
      await linkCuisineTypeForRestaurant(r.id, place);
    } catch (err) {
      console.error("[backfill] error processing", r.id, err);
    }
  }
  console.log("[backfill] done");

  // Diagnostic: list ALL restaurant_cuisine_types rows after backfill.
  const { data: allLinks, error: linksErr } = await supabase
    .from("restaurant_cuisine_types")
    .select("restaurant_id, cuisine_type_id");
  console.log("[backfill] all rct rows after run:", allLinks?.length ?? 0, allLinks, linksErr);
}

